import React, { FunctionComponent, useState, useEffect, useCallback, useRef } from 'react';
import { Presentation } from './presentation';
import { ApiBody, DeletableKeys } from '../../../types/personShow';
import type { PersonShowProps as Props, PersonShowState } from './types';
import { useDevice } from '../../../hooks/device';
import {
  fetchSidebarDetails,
  uploadIcon,
  fetchDetails,
  updateApi,
  sendRequest,
  fetchEntriesForCompany,
} from '../../../services/personProfiles';
import {
  requestResume,
  sendResumes,
  fetchResume,
} from '../../../services/resumes';
import {
  requestJobResume,
  sendJobResumes,
  fetchJobResume,
} from '../../../services/jobResumes';
import {
  postLike
} from '../../../services/scouts';
import { getSelectedStatusName } from '../../../services/companies';
import { fetchCompanyPersonMemo, upsertCompanyPersonMemo } from '../../../services/companyPersonMemos';
import { updatePersonConnectRecruitments } from '../../../services/personConnectRecruitments';
import { getCsrfToken } from '../../../libs/csrfToken';
import { SelectedStatus } from '../../../types/entries';
import { ApplyItem, EventItem } from '../../organisms/personShowDetails/types';
import { isEndAfterStart, isValidYearMonth } from '../../../libs/dateUtils';

export const Component: FunctionComponent<Props> = ({
    personId,
    logoPc,
    logoSp,
    noIconPersonImg,
    instagramIcon,
    twitterIcon,
    xIcon,
    penIconImg,
    offline011,
    unCompletedProfile,
    birthdayList,
    birthdayListStart,
    startYearList,
    endYearList,
    graduationList,
    careerEmploymentTypes,
    messageBoxId,
    isAllowedToScout,
    isMinimum,
    isSuccessPlan,
    isAllowedToSendScout,
    isOneYearPassed,
    isShow,
    fromPage,
    recentlyRegistered,
    loadingImagePath,
    isHidden,
    isAlreadyConnected,
    isNotRequested,
    isStudent,
    isNotRequestedResume,
    isNotRequestedJobResume,
    positions,
  }) => {
  const { isPc } = useDevice();
  const [state, setState] = useState<PersonShowState>({
    icon: '',
    name: '',
    follows: null,
    gender: '',
    location: '',
    birthday: '',
    age: '',
    graduationYear: '',
    tel: '',
    mail: '',
    instagram: '',
    twitter: '',
    currentOccupations: [],
    salary: null,
    salaries: [],
    introduction: '',
    introductionRaw: '',
    educationalHistories: [],
    careerHistories: [],
    activity: '',
    slogan: '',
    interests: [],
    favoriteBrands: [],
    skills: [],
    motivation: '',
    preferredLocations: [],
    preferredOccupations: [],
    preferenceConditions: [],
    flash: false,
    flashType: '',
    flashMessage: '',
    resumeLoading: false,
    isModal: false,
    modalTitle: '',
    target: '',
    clickTarget: 0,
    resume: {
      resume: '',
    },
    job_resume: {
      job_resume: ''
    },
  });
  const [prevIconUrl, setPrevIconUrl] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<SelectedStatus[]>([]);

  // Memo state for company view
  const [memo, setMemo] = useState('');
  const [applies, setApplies] = useState<ApplyItem[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
  const memoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 社員の公開プロフィール(company_people#show)は履歴書欄を表示しない(isHidden: true)うえ、
    // 未ログインでも閲覧できるため、呼んでも401/404になるだけの履歴書APIは呼ばない
    if (fromPage !== 'companyPeople') {
      resumeStates();
      jobResumeStates();
    }
    fetchSelectedStatuses();
    if (fromPage !== 'personShow') {
      fetchMemoData();
      fetchEntriesData();
    }
  }, []);

  // Cleanup memo timeout on unmount
  useEffect(() => {
    return () => {
      if (memoTimeoutRef.current) {
        clearTimeout(memoTimeoutRef.current);
      }
    };
  }, []);

  const fetchMemoData = async () => {
    try {
      const response = await fetchCompanyPersonMemo(personId);
      setMemo(response.content || '');
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEntriesData = async () => {
    try {
      const response = await fetchEntriesForCompany(personId);
      setApplies(response.applies);
      setEvents(response.events);
    } catch (error) {
      console.log(error);
    }
  };

  const handleMemoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMemo(value);

    if (memoTimeoutRef.current) {
      clearTimeout(memoTimeoutRef.current);
    }

    memoTimeoutRef.current = setTimeout(async () => {
      try {
        await upsertCompanyPersonMemo(personId, value, getCsrfToken());
      } catch (error) {
        console.log(error);
      }
    }, 500);
  };

  const handleOpenMemoModal = () => {
    setIsMemoModalOpen(true);
    $('body, html').css('overflow', 'hidden');
  };

  const handleCloseMemoModal = () => {
    setIsMemoModalOpen(false);
    $('body, html').css('overflow', 'auto');
  };

  const fetchValue = (selectionStatus: string, processingSelectionStatus: string) => {
    return selectionStatus === 'processing' && processingSelectionStatus
      ? processingSelectionStatus
      : selectionStatus;
  };

  const handleSelectionStatusChanged = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    let fetchBody: Record<string, any> = {};
    const value = e.target.value;
    const isProcessing = ['outstanding', 'processing', 'adopted', 'not_adopted'].includes(value);
    const connectId = e.currentTarget.dataset.id;
    if (!connectId) return;

    if (isProcessing) {
      fetchBody = { selection_status_id: value };
    } else {
      fetchBody = {
        selection_status_id: 'processing',
        processing_selection_status_id: value,
      };
    }

    try {
      await updatePersonConnectRecruitments(Number(connectId), JSON.stringify(fetchBody), getCsrfToken());
      fetchEntriesData();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSelectedStatuses = async () => {
    try {
      const response = await getSelectedStatusName();
      setSelectedStatuses(response.selected_statuses || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenModal = (target: string, title: string) => {
    if (target !== undefined && title !== undefined) {
      if (!state.isModal) {
        $('body, html').css('overflow', 'hidden')
      } else {
        $('body, html').css('overflow', 'auto')
      }
      setState(prev => ({
        ...prev,
        isModal: !state.isModal,
        modalTitle: title,
        target: target,
      }));
      if (target !== 'preferredOccupations') {
        handleFetchDetails();
      }
      handleFetchSidebarDetails();
    }
  };

  const handleRequestResume = async (type: string) => {
    try {
      const csrfToken = getCsrfToken();
      const response =
        type === 'resume'
          ? await requestResume(personId, csrfToken)
          : await requestJobResume(personId, csrfToken);
      if (response.status === 'success') {
        setState(prev => ({
          ...prev,
          flash: response.flash,
          flashType: response.flashType,
          flashMessage: response.flashMessage
        }));
      } else if (response.status === 'bad_request') {
        alert(response.error_messages);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFetchSidebarDetails = async () => {
    try {
      const response = await fetchSidebarDetails(personId);
      // prevIconUrlと異なる場合だけセット（巻き戻し防止）
      const updatedIcon = prevIconUrl !== response.icon ? response.icon : state.icon;
      setState(prev => ({
        ...prev,
        icon: updatedIcon,
        name: response.name,
        follows: response.follows,
        gender: response.gender,
        location: response.location,
        birthday: response.birthday,
        age: response.age,
        graduationYear: response.graduationYear,
        tel: response.tel,
        mail: response.mail,
        instagram: response.instagram,
        twitter: response.twitter,
        currentOccupations: response.currentOccupations,
        salary: response.salary,
        salaries: response.salaries
      }));
      $('.person__show__sidebar__heading .spinner').removeClass('active');
      $('.person__show__sidebar__heading--icon--img').removeClass('blur');
      $('.person__show__sidebar__heading--icon--camera').removeClass('blur');
    } catch (error) {
      console.log(error);
    }
  };

  const handleUploadIcon = async (file: File) => {
    $('.person__show__sidebar__heading .spinner').addClass('active');
    $('.person__show__sidebar__heading--icon--img').addClass('blur');
    $('.person__show__sidebar__heading--icon--camera').addClass('blur');
    const previewUrl = URL.createObjectURL(file);
    try {
      await uploadIcon(file, getCsrfToken());
      setPrevIconUrl(state.icon);
      setState(prev => ({ ...prev, icon: previewUrl }));
      setTimeout(() => URL.revokeObjectURL(previewUrl), 5000); // 5秒後にメモリ解放
    } catch (error) {
      if (error) {
        window.alert('リクエストの送信に失敗しました、再度お試しください');
      }
    } finally {
      $('.person__show__sidebar__heading .spinner').removeClass('active');
      $('.person__show__sidebar__heading--icon--img').removeClass('blur');
      $('.person__show__sidebar__heading--icon--camera').removeClass('blur');
    }
  };

  const handleFetchDetails = async () => {
    try {
      const response = await fetchDetails(personId)
      setState(prev => ({
        ...prev,
        introduction: response.introduction,
        introductionRaw: response.introductionRaw,
        educationalHistories: response.educationalHistories,
        careerHistories: response.careerHistories,
        activity: response.activity,
        slogan: response.slogan,
        interests: response.interests,
        favoriteBrands: response.favoriteBrands,
        skills: response.skills,
        motivation: response.motivation,
        preferredLocations: response.preferredLocations,
        preferredOccupations: response.preferredOccupations,
        preferenceConditions: response.preferenceConditions
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = (target: string, deleted: boolean, overrideValue?: any) => {
    if (target === 'resume') {
      handleSendResumes();
    } else if (target === 'job_resume') {
      handleSendJobResumes();
    } else if (target === 'birthday') {
      if (state.birthday === '') {
        alert('生年月日は必須項目です。選択してから保存してください。')
        return false
      } else {
        handleUpdateApi({ target: target, value: state[target] }, target)
      }
    } else if (target === 'educationalHistories') {
      if (deleted) {
        handleUpdateApi({ target: target, value: state[target] }, target)
      } else {
        const targetHistory = state[target][state.clickTarget]
        if (!targetHistory) {
          alert('学校名 / 在籍期間は必須項目です。追加・選択してから保存してください。')
          return false
        } else if (targetHistory.name === '' && targetHistory.startDate === '' && targetHistory.endDate === '') {
          alert('学校名 / 在籍期間は必須項目です。追加・選択してから保存してください。')
          return false
        } else if (targetHistory.name === '') {
          alert('学校名は必須項目です。追加・選択してから保存してください。')
          return false
        } else if (targetHistory.startDate === '/' || targetHistory.startDate === '' || targetHistory.startDate.includes('NaN')) {
          alert('在籍期間は必須項目です。追加・選択してから保存してください。')
          return false
        } else if (targetHistory.endDate === '現在' || targetHistory.endDate.includes('NaN')) {
          alert('在籍期間は必須項目です。追加・選択してから保存してください。')
          return false
        } else if (!isValidYearMonth(targetHistory.startDate)) {
          alert('在籍期間が正しく入力されていません。')
          return false
        } else if (!isValidYearMonth(targetHistory.endDate)) {
          alert('在籍期間が正しく入力されていません。')
          return false
        } else if (!isEndAfterStart(targetHistory.startDate, targetHistory.endDate)) {
          alert('在籍期間の終了は開始より後の日付を選択してください。')
          return false
        } else {
          handleUpdateApi({ target: target, value: state[target] }, target)
        }
      }
    } else if (target === 'careerHistories') {
      if (deleted) {
        handleUpdateApi({ target: target, value: state[target] }, target)
      } else {
        const targetHistory = state[target][state.clickTarget]
        if (
          !targetHistory ||
          (
            ['', null].includes(targetHistory.name) &&
            ['', null].includes(targetHistory.startDate) &&
            ['', null].includes(targetHistory.endDate) &&
            !targetHistory.careerEmploymentTypeId &&
            targetHistory.position.length === 0
          )
        ) {
          alert('会社名 / 在籍期間 / 雇用形態 / 職種は必須項目です。追加・選択してから保存してください。');
          return false;
        }
        const missingFields: string[] = [];
        // 会社名チェック
        if (!targetHistory.name || targetHistory.name.trim() === '') {
          missingFields.push('会社名');
        }
        // 在籍期間チェック（開始日）
        if (
          !targetHistory.startDate ||
          targetHistory.startDate === '/' ||
          targetHistory.startDate === '' ||
          targetHistory.startDate.includes('NaN')
        ) {
          missingFields.push('在籍期間');
        }
        // 雇用形態チェック
        if (!targetHistory.careerEmploymentTypeId) {
          missingFields.push('雇用形態');
        }
        // 職種チェック
        if (!targetHistory.position || targetHistory.position.length === 0) {
          missingFields.push('職種');
        }
        // 不足項目がある場合まとめてアラート
        if (missingFields.length > 0) {
          alert(`${missingFields.join(' / ')} は必須項目です。追加・選択してから保存してください。`);
          return false;
        }
        if (!isValidYearMonth(targetHistory.startDate)) {
          alert('在籍期間が正しく入力されていません。');
          return false;
        }
        if (!targetHistory.currentWorking) {
          // 「現在働いている」未選択の場合のみ終了日のバリデーション
          if (
            !targetHistory.endDate ||
            targetHistory.endDate === '/' ||
            targetHistory.endDate === '' ||
            targetHistory.endDate.includes('NaN')
          ) {
            alert('在籍期間が正しく入力されていません。');
            return false;
          } else if (!isValidYearMonth(targetHistory.endDate)) {
            alert('在籍期間（終了年/月）が正しく入力されていません。');
            return false;
          } else if (!isEndAfterStart(targetHistory.startDate, targetHistory.endDate)) {
            alert('在籍期間の終了は開始より後の日付を選択してください。');
            return false;
          }
        }

        handleUpdateApi({ target: target, value: state[target] }, target)
      }
    } else if (target === 'currentOccupations') {
      if (state.currentOccupations.length === 0 && state.currentOccupationDetails?.length === 0) {
        alert('直近の職種は必須項目です。選択してから保存してください。')
        return false
      } else {
        handleUpdateApi({ target: target, value: state[target] as string[], currentOccupationDetails: state.currentOccupationDetails }, target)
      }
    } else if (target === 'preferredOccupations') {
      if (state.preferredOccupations.length === 0 && state.preferredOccupationDetails?.length === 0) {
        alert('希望職種は必須項目です。選択してから保存してください。')
        return false
      } else {
        handleUpdateApi({ target: target, value: state[target] as string[], preferredOccupationDetails: state.preferredOccupationDetails }, target)
      }
    } else if (target === 'preferredLocations') {
      if (state.preferredLocations.length === 0) {
        alert('希望勤務地は必須項目です。選択してから保存してください。')
        return false
      } else {
        handleUpdateApi({ target: target, value: state[target] }, target)
      }
    } else if (target === 'graduationYear') {
      if (state.graduationYear === '') {
        alert('卒業(予定)年は必須項目です。選択してから保存してください。')
        return false
      } else {
        handleUpdateApi({ target: target, value: state[target] }, target)
      }
    } else {
      const key = target as keyof PersonShowState;
      const value = overrideValue !== undefined ? overrideValue : state[key];
      if (overrideValue !== undefined) {
        setState(prev => ({ ...prev, [key]: overrideValue }));
      }
      handleUpdateApi({ target: target, value: value as any }, target)
    }
  };

  const handleUpdateApi = async (body: ApiBody, target: string) => {
    try {
      const response = await updateApi(personId, body, getCsrfToken());
      setState(prev => {
        const updatedState = { ...prev };
        if (target === 'preferredOccupations' && 'preferredOccupations' in response) {
          updatedState.preferredOccupations = response.preferredOccupations;
        } else if (target === 'currentOccupations' && 'currentOccupations' in response) {
          updatedState.currentOccupations = response.currentOccupations;
        } else if (target === 'educationalHistories' || target === 'careerHistories') {
          // startDateの昇順でソート（古い順）
          const sortByStartDate = <T extends { startDate?: string }>(arr: T[] = []): T[] => {
            return [...arr].sort(
              (a, b) => toYearMonthValue(a.startDate) - toYearMonthValue(b.startDate)
            );
          };
          if (target === 'educationalHistories' && Array.isArray(prev.educationalHistories)) {
            updatedState.educationalHistories = sortByStartDate(prev.educationalHistories);
          } else if (target === 'careerHistories' && Array.isArray(prev.careerHistories)) {
            updatedState.careerHistories = sortByStartDate(prev.careerHistories);
          }
        }
        return updatedState;
      });
      setState(prev => ({ ...prev, isModal: false, target: '' }));
      $('body, html').css('overflow', 'auto');
    } catch (error) {
      console.log(error);
    }
  };

  const toYearMonthValue = (value?: string): number => {
    if (!value) return 0;
    const [yearStr, monthStr] = value.split('/');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (isNaN(year) || isNaN(month)) return 0;
    return new Date(year, month - 1).getTime();
  };

  const handleSendResumes = async () => {
    setState(prev => ({ ...prev, resumeLoading: true }));
    // 保存されているが、ただモーダルを開いて保存ボタンを押してもエラーにならないためのエラーハンドリング
    // ResumeStates オブジェクト
    const r = state.resume;
    // 中身は File | ''。型ガードで分岐
    const value =
      typeof r === 'object' && r !== null && 'resume' in r
        ? r.resume
        : r; // r 自体が空文字のケースに対応
    // ファイル未選択（変更なし）なら閉じて終了
    if (!(value instanceof File) && value !== '') {
      setState(prev => ({ ...prev, isModal: false, resumeLoading: false }));
      $('body, html').css('overflow', 'auto');
      return;
    }
    const file = value instanceof File ? value : '';
    try {
      const response = await sendResumes(personId, file, getCsrfToken());
      setState(prev => ({
        ...prev,
        resume: response.states,
        isModal: false
      }));
      $('body, html').css('overflow', 'auto');
    } catch (error) {
      window.alert(`問題が発生しました${error}`);
    } finally {
      setState(prev => ({ ...prev, resumeLoading: false }));
    }
  };

  const handleSendJobResumes = async () => {
    setState(prev => ({ ...prev, resumeLoading: true }));
    // 保存されているが、ただモーダルを開いて保存ボタンを押してもエラーにならないためのエラーハンドリング
    // JobResumeStates オブジェクト
    const r = state.job_resume;
    // 中身は File | ''。型ガードで分岐
    const value =
      typeof r === 'object' && r !== null && 'job_resume' in r
        ? r.job_resume
        : r; // r 自体が空文字のケースに対応
    // ファイル未選択（変更なし）なら閉じて終了
    if (!(value instanceof File) && value !== '') {
      setState(prev => ({ ...prev, isModal: false }));
      $('body, html').css('overflow', 'auto');
      return;
    }
    const file = value instanceof File ? value : '';
    try {
      const response = await sendJobResumes(personId, file, getCsrfToken());
      setState(prev => ({
        ...prev,
        job_resume: response.states,
        isModal: false
      }));
      $('body, html').css('overflow', 'auto');
    } catch (error) {
      window.alert(`問題が発生しました${error}`);
    } finally {
      setState(prev => ({ ...prev, resumeLoading: false }));
    }
  };

  const resumeStates = async () => {
    try {
      const response = await fetchResume(personId);
      setState(prev => ({ ...prev, resume: response.states }));
    } catch (error) {
      console.log(error);
    }
  };

  const jobResumeStates = async () => {
    try {
      const response = await fetchJobResume(personId);
      setState(prev => ({ ...prev, job_resume: response.states }));
    } catch (error) {
      console.log(error);
    }
  };

  const onChangedBody = (name: string, value: any) => {
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const onChangeClickTarget = (value: number) => {
    setState((prev) => ({ ...prev, clickTarget: value }));
  };

  const handleSendRequest = async () => {
    try {
      const response = await sendRequest(personId, getCsrfToken());
      if (response.status === 'success') {
        setState(prev => ({
          ...prev,
          flash: response.flash,
          flashType: response.flashType,
          flashMessage: response.flashMessage
        }));
      } else if (response.status === 'failed') {
        alert(response.error_messages);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLike = async () => {
    try {
      const response = await postLike(personId, getCsrfToken())
      setState(prev => ({
        ...prev,
        flash: response.flash,
        flashType: response.flashType,
        flashMessage: response.flashMessage
      }));
    } catch (error) {
      console.error(error)
      alert('気になるの送信に失敗しました、再度お試しください')
    }
  };

  const insertFlashScout = () => {
    if (state.flash) {
      setState(prev => ({
        ...prev,
        flash: false,
        flashType: '',
        flashMessage: ''
      }));
    }
    setState(prev => ({
      ...prev,
      flash: true,
      flashType: 'success',
      flashMessage: 'スカウトメールを送信しました、応募を待ちましょう'
    }));
  };

  const handleDeleteHistories = () => {
    setState(prev => ({...prev, isModal: false}));
    $('body, html').css('overflow', 'auto');
    const target = state.target as DeletableKeys;
    const targetArray = state[target];
    const index = state.clickTarget;
    targetArray.splice(index, 1);
    setState(prev => ({...prev, [target]: targetArray}));
    handleUpdate(target, true)
  }

  const showOffline011Banner =
    typeof state.graduationYear !== 'undefined' &&
    String(state.graduationYear).includes('2028');

  return (
    <Presentation
      state={state}
      isPc={isPc}
      showOffline011Banner={showOffline011Banner}
      personId={personId}
      logoPc={logoPc}
      logoSp={logoSp}
      noIconPersonImg={noIconPersonImg}
      instagramIcon={instagramIcon}
      twitterIcon={twitterIcon}
      xIcon={xIcon}
      penIconImg={penIconImg}
      offline011={offline011}
      unCompletedProfile={unCompletedProfile}
      birthdayList={birthdayList}
      birthdayListStart={birthdayListStart}
      startYearList={startYearList}
      endYearList={endYearList}
      graduationList={graduationList}
      careerEmploymentTypes={careerEmploymentTypes}
      messageBoxId={messageBoxId}
      isAllowedToScout={isAllowedToScout}
      isMinimum={isMinimum}
      isSuccessPlan={isSuccessPlan}
      isAllowedToSendScout={isAllowedToSendScout}
      isOneYearPassed={isOneYearPassed}
      fromPage={fromPage}
      recentlyRegistered={recentlyRegistered}
      loadingImagePath={loadingImagePath}
      isHidden={isHidden}
      isAlreadyConnected={isAlreadyConnected}
      isNotRequested={isNotRequested}
      isNotRequestedResume={isNotRequestedResume}
      isNotRequestedJobResume={isNotRequestedJobResume}
      positions={positions}
      isShow={isShow}
      isStudent={isStudent}
      handleOpenModal={handleOpenModal}
      handleRequestResume={handleRequestResume}
      handleFetchSidebarDetails={handleFetchSidebarDetails}
      handleUploadIcon={handleUploadIcon}
      handleFetchDetails={handleFetchDetails}
      handleUpdate={handleUpdate}
      onChangedBody={onChangedBody}
      onChangeClickTarget={onChangeClickTarget}
      handleSendRequest={handleSendRequest}
      handleLike={handleLike}
      insertFlashScout={insertFlashScout}
      handleDeleteHistories={handleDeleteHistories}
      selected_statuses={selectedStatuses}
      memo={memo}
      applies={applies}
      events={events}
      isMemoModalOpen={isMemoModalOpen}
      handleMemoChange={handleMemoChange}
      handleOpenMemoModal={handleOpenMemoModal}
      handleCloseMemoModal={handleCloseMemoModal}
      handleSelectionStatusChanged={handleSelectionStatusChanged}
      fetchValue={fetchValue}
    />
  );
};
